import React from "react";
import PropTypes from "prop-types";
import { Link, Redirect } from "react-router-dom";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

import debounce from "lodash/debounce";

import AuthOptions from "../../auth/AuthOptions";
import { useAuthState } from "../../app/AuthStateContext";
import usePublicStats from "../../public-site/usePublicStats";
import { useTemplateTrack } from "../TemplateTrackingProvider";
import { FAQ_ITEMS } from "../../public-site/siteData";
import { getTemplateSx, useFadeInOnScroll } from "./styles";

const threeThings = [
  {
    number: "01",
    title: "Every signup is human-reviewed",
    body: "Before the first match, every player goes through a human review. ID checks are optional. Not a bot check",
  },
  {
    number: "02",
    title: "Every report is human-reviewed",
    body: "Not auto-resolved by a point system. A person reads it",
  },
  {
    number: "03",
    title: "No client-side anti-cheat",
    body: "We collect tick-by-tick data from the server. Your machine stays yours",
  },
];

function stripDots(text) {
  return typeof text === "string" ? text.replace(/\./g, "") : text;
}

export default function OriginalHomePage({ catalog, templateReady = true }) {
  const sx = React.useMemo(() => getTemplateSx(), []);
  const { isAuthenticated } = useAuthState();
  const { user } = catalog;
  const track = useTemplateTrack();
  const stats = usePublicStats();

  const totalMembers = stats?.verifiedTotal;
  const discordOnline = stats?.discord?.onlineCount;
  const discordInviteUrl = stats?.discord?.inviteUrl;
  const hasTrustStrip = totalMembers != null || discordOnline != null;

  const section2Ref = React.useRef(null);
  const section3Ref = React.useRef(null);
  const section5Ref = React.useRef(null);
  const section6Ref = React.useRef(null);

  useFadeInOnScroll(section2Ref);
  useFadeInOnScroll(section3Ref);
  useFadeInOnScroll(section5Ref);
  useFadeInOnScroll(section6Ref);

  React.useEffect(() => {
    if (!templateReady) return;
    track("homepage_view");
  }, [templateReady, track]);

  const firedMilestonesRef = React.useRef(new Set());
  React.useEffect(() => {
    function onScroll() {
      const scrolled = window.scrollY + window.innerHeight;
      const total = document.documentElement.scrollHeight;
      if (!total) return;
      const pct = (scrolled / total) * 100;
      for (const milestone of [25, 50, 75, 90]) {
        if (pct >= milestone && !firedMilestonesRef.current.has(milestone)) {
          firedMilestonesRef.current.add(milestone);
          track("scroll_milestone", { meta: { depth: milestone } });
        }
      }
    }
    const debouncedScroll = debounce(onScroll, 250);
    window.addEventListener("scroll", debouncedScroll, { passive: true });
    return () => window.removeEventListener("scroll", debouncedScroll);
  }, [track]);

  if (isAuthenticated && user?.email) {
    return <Redirect to="/lobby" />;
  }

  return (
    <Box sx={sx.pageRoot}>
      <Container maxWidth="xl">
        {/* Hero */}
        <Box sx={sx.hero} id="get-started">
          <Box sx={sx.heroInner}>
            <Box
              component="img"
              alt="Ouro"
              src="https://ouro-public-assets.s3.eu-west-2.amazonaws.com/init-logo-darkmode.png"
              sx={sx.heroLogo}
            />

            <Typography component="h1" sx={sx.h1}>
              <Box component="span" sx={sx.h1Line}>
                Better Matches
              </Box>
              <Box component="span" sx={sx.h1Line}>
                Real people
              </Box>
            </Typography>

            <Typography sx={sx.sub}>
              <Box component="span" sx={sx.subLine}>
                CS2 matchmaking for verified players
              </Box>
              <Box component="span" sx={sx.subLine}>
                Every signup reviewed by a human
              </Box>
              <Box component="span" sx={sx.subLine}>
                Every report read by a human
              </Box>
              <Box component="span" sx={sx.subLine}>
                Server-side anti-cheat, no client install
              </Box>
            </Typography>

            <Box sx={sx.ctaRow}>
              <AuthOptions surface="hero" />
            </Box>
          </Box>
        </Box>

        {/* Trust strip */}
        {hasTrustStrip ? (
          <Box sx={sx.trustStrip}>
            <Box sx={sx.trustStripInner}>
              {totalMembers != null ? (
                <Box sx={sx.trustStat}>
                  <Typography sx={sx.trustValue}>
                    {totalMembers.toLocaleString()}
                  </Typography>
                  <Typography component="span" sx={sx.trustLabel}>
                    members
                  </Typography>
                </Box>
              ) : null}

              {discordOnline != null ? (
                <Box sx={sx.trustStat}>
                  <Typography sx={sx.trustValue}>
                    {discordOnline.toLocaleString()}
                  </Typography>
                  {discordInviteUrl ? (
                    <Typography
                      component="a"
                      href={discordInviteUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => track("discord_clicked", { surface: "trust_strip" })}
                      sx={{ ...sx.trustLabel, ...sx.trustLabelLink }}
                    >
                      <Box component="span" sx={sx.liveDot} />
                      online in Discord
                    </Typography>
                  ) : (
                    <Typography component="span" sx={sx.trustLabel}>
                      <Box component="span" sx={sx.liveDot} />
                      online in Discord
                    </Typography>
                  )}
                </Box>
              ) : null}
            </Box>
          </Box>
        ) : null}

        {/* Why Ouro exists */}
        <Box ref={section2Ref} sx={sx.section}>
          <Typography sx={sx.sectionTitle}>
            Built for solo play without the suffering
          </Typography>
          <Typography sx={sx.sectionCopy}>
            Every player is identity-verified and human-reviewed before their
            first match No client to install No kernel driver on your machine No
            tournaments Tournaments invite exactly the kinds of players we are
            filtering out
          </Typography>
        </Box>

        {/* Three things that are different */}
        <Box ref={section3Ref} sx={sx.section}>
          <Box sx={sx.threeGrid}>
            {threeThings.map((item) => (
              <Box key={item.number}>
                <Typography component="span" sx={sx.thingNumber}>
                  {item.number}
                </Typography>
                <Typography sx={sx.thingTitle}>{item.title}</Typography>
                <Typography sx={sx.thingBody}>{item.body}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* FAQ */}
        <Box ref={section5Ref} sx={sx.section}>
          <Typography sx={sx.sectionTitle}>Common questions</Typography>
          <Box sx={sx.faqGrid}>
            {FAQ_ITEMS.slice(0, 4).map((item) => (
              <Box key={item.question} sx={sx.faqItem}>
                <Typography sx={sx.faqQuestion}>
                  {stripDots(item.question)}
                </Typography>
                <Typography sx={sx.faqAnswer}>
                  {stripDots(item.answer)}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={sx.faqLink}>
            <Button
              component={Link}
              to="/faq"
              variant="outlined"
              sx={sx.secondaryButton}
            >
              Read the full FAQ
            </Button>
          </Box>
        </Box>

        {/* Final CTA */}
        <Box ref={section6Ref} sx={sx.callout}>
          <Typography sx={sx.calloutTitle}>Play CS2 properly</Typography>
          <AuthOptions surface="footer_callout" />
        </Box>
      </Container>
    </Box>
  );
}

OriginalHomePage.propTypes = {
  catalog: PropTypes.shape({
    user: PropTypes.object,
  }).isRequired,
  templateReady: PropTypes.bool,
};
